import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getEsignRecipientView, signEsignRequestInternal, declineEsignRequestInternal } from "../api";
import { useToast } from "../context/ToastContext";
import { LoadingOverlay } from "../components/LoadingOverlay";
import { OfferPdfViewer } from "../components/OfferPdfViewer";
import { SignaturePad } from "../components/SignaturePad";

const API_BASE = (process.env.REACT_APP_API_URL || "http://localhost:30801");

/** Internal (authenticated, in-app) recipient signing page — the target of
 * the link emailed by notify_esign_signature_requested for internal
 * employees. External recipients use PublicEsignSignPage instead. */
export default function EsignSignPage() {
  const { requestId, recipientId } = useParams<{ requestId: string; recipientId: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const [readToEnd, setReadToEnd] = useState(false);
  const [typedName, setTypedName] = useState("");
  const [signatureDrawn, setSignatureDrawn] = useState<string | null>(null);
  const [consentChecked, setConsentChecked] = useState(false);
  const [declining, setDeclining] = useState(false);
  const [declineReason, setDeclineReason] = useState("");
  const [busy, setBusy] = useState(false);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["esign-recipient-view", requestId, recipientId],
    queryFn: () => getEsignRecipientView(Number(requestId), Number(recipientId)),
    enabled: !!requestId && !!recipientId,
  });

  const req = (data as any)?.request;
  const myRole = (data as any)?.my_role;
  const me = req?.recipients?.find((r: any) => r.role === myRole);
  const notMyTurn = me?.status === "pending";
  const alreadyDone = me && ["signed", "declined"].includes(me.status);
  const pdfUrl = `${API_BASE}/api/esign/requests/${requestId}/pdf`;

  async function handleSign() {
    setBusy(true);
    try {
      await signEsignRequestInternal(Number(requestId), Number(recipientId), typedName.trim(), signatureDrawn, consentChecked);
      toast("Signed", "success");
      refetch();
    } catch (e: any) {
      toast(e?.response?.data?.detail || "Failed to sign", "error");
    } finally {
      setBusy(false);
    }
  }

  async function handleDecline() {
    if (!declineReason.trim()) return;
    setBusy(true);
    try {
      await declineEsignRequestInternal(Number(requestId), Number(recipientId), declineReason.trim());
      toast("Declined", "success");
      refetch();
    } catch (e: any) {
      toast(e?.response?.data?.detail || "Failed to decline", "error");
    } finally {
      setBusy(false);
    }
  }

  const canSign = readToEnd && !!typedName.trim() && consentChecked && !busy;

  return (
    <div style={{ padding: 24, maxWidth: 760, margin: "0 auto" }}>
      <LoadingOverlay active={busy} />
      <button onClick={() => navigate("/esign")} style={{ border: "none", background: "none", color: "#64748b", fontSize: 13, cursor: "pointer", marginBottom: 12, padding: 0 }}>
        ← Back to Document Signing
      </button>

      {isLoading && <p style={{ fontSize: 13, color: "#64748b" }}>Loading…</p>}
      {isError && <p style={{ fontSize: 13, color: "#dc2626" }}>This document couldn't be loaded — it may not be addressed to you.</p>}

      {!isLoading && !isError && req && (
        <div style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 12, padding: 24 }}>
          <h2 style={{ margin: "0 0 4px", fontSize: 18, color: "#0f172a" }}>{req.title}</h2>
          <p style={{ margin: "0 0 16px", fontSize: 12, color: "#64748b" }}>Signing as <strong>{myRole}</strong></p>

          {alreadyDone ? (
            <div style={{ textAlign: "center", padding: "32px 0" }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>{me.status === "signed" ? "✅" : "👋"}</div>
              <p style={{ margin: 0, fontSize: 14, color: "#475569" }}>
                {me.status === "signed" ? "You've already signed this document." : "You declined to sign this document."}
              </p>
            </div>
          ) : notMyTurn ? (
            <div style={{ textAlign: "center", padding: "32px 0" }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>⏳</div>
              <p style={{ margin: 0, fontSize: 14, color: "#475569" }}>
                It's not your turn yet — waiting on an earlier signer. You'll be notified once it's ready for you.
              </p>
            </div>
          ) : (
            <>
              <OfferPdfViewer url={pdfUrl} onReadToEnd={() => setReadToEnd(true)} />
              {!readToEnd && (
                <p style={{ margin: "8px 0 0", fontSize: 12, color: "#94a3b8", textAlign: "center" }}>
                  ↓ Scroll through the full document to enable signing
                </p>
              )}

              {!declining ? (
                <>
                  <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, padding: "16px 18px", margin: "16px 0" }}>
                    <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#475569", marginBottom: 6 }}>Sign</label>
                    <input
                      value={typedName} onChange={e => setTypedName(e.target.value)}
                      placeholder="Type your full name"
                      style={{ width: "100%", padding: "10px 12px", borderRadius: 8, fontSize: 14, border: "1.5px solid #e2e8f0", outline: "none", color: "#1e293b", boxSizing: "border-box", marginBottom: 12 }}
                    />
                    <SignaturePad onChange={setSignatureDrawn} />
                    <label style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 12, color: "#475569", marginTop: 12, cursor: "pointer" }}>
                      <input type="checkbox" checked={consentChecked} onChange={e => setConsentChecked(e.target.checked)} style={{ marginTop: 2 }} />
                      I agree that clicking below constitutes my electronic signature on this document.
                    </label>
                  </div>
                  <div style={{ display: "flex", gap: 12 }}>
                    <button onClick={handleSign} disabled={!canSign}
                      style={{ flex: 1, padding: "12px 0", borderRadius: 10, border: "none", background: !canSign ? "#94a3b8" : "linear-gradient(135deg,#15803d,#22c55e)", color: "white", fontWeight: 700, fontSize: 14, cursor: !canSign ? "not-allowed" : "pointer" }}>
                      ✅ Sign
                    </button>
                    <button onClick={() => setDeclining(true)} disabled={busy}
                      style={{ flex: 1, padding: "12px 0", borderRadius: 10, border: "2px solid #fecaca", background: "white", color: "#dc2626", fontWeight: 700, fontSize: 14, cursor: "pointer" }}>
                      Decline
                    </button>
                  </div>
                </>
              ) : (
                <div style={{ marginTop: 16 }}>
                  <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#475569", marginBottom: 6 }}>Reason for declining</label>
                  <textarea value={declineReason} onChange={e => setDeclineReason(e.target.value)} rows={3}
                    style={{ width: "100%", padding: "10px 12px", borderRadius: 8, fontSize: 13, border: "1.5px solid #e2e8f0", outline: "none", color: "#1e293b", boxSizing: "border-box" }} />
                  <div style={{ display: "flex", gap: 12, marginTop: 12 }}>
                    <button onClick={handleDecline} disabled={!declineReason.trim() || busy}
                      style={{ flex: 1, padding: "10px 0", borderRadius: 8, border: "none", background: !declineReason.trim() ? "#94a3b8" : "#dc2626", color: "white", fontWeight: 700, fontSize: 13, cursor: !declineReason.trim() ? "not-allowed" : "pointer" }}>
                      Confirm Decline
                    </button>
                    <button onClick={() => setDeclining(false)} style={{ flex: 1, padding: "10px 0", borderRadius: 8, border: "1px solid #e2e8f0", background: "white", fontSize: 13, cursor: "pointer" }}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
